import { gsap } from "gsap";
import { Container, IDestroyOptions, Renderer, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { IAnalyticProviderWrapper } from "../../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../../analytics/events/VirtualScreenEvent";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { nameof } from "../../../../../shared/bindable-data";
import { ILogger } from "../../../../logger/ILogger";
import { RenterPolicyCycleResult } from "../../../../networking/data/responses/renterPolicy/RenterPolicyCycleResult";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { GameFont } from "../../../common/GameFont";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { DefaultButton } from "../../../common/components/DefaultButton";
import { UIScrollBox } from "../../../common/components/UIScrollBox";
import { VerticalScrollBar } from "../../../common/components/VerticalScrollBar";
import { IFirstTimeExperienceController } from "../../../common/controller/IFirstTimeExperienceController";
import { IBuildingModel } from "../../../common/models/Building/IBuildingModel";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { IRenterPolicyModel } from "../../../common/models/Renters/IRenterPolicyModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { IGameController } from "../../../game/controllers/IGameController";
import { PiggyAnimationPromiseHolder } from "../../buildingUpgradeItem/components/PiggyAnimationPromiseHolder";
import { Building } from "../components/Building";
import { NextBuilding } from "../components/NextBuilding";
import { MyBuildingsTutorial } from "../components/tutorial/MyBuildingsTutorial";
import { IMyBuildingsController } from "../controller/IMyBuildingsController";
import { MyBuildingsResourcePackage } from "../resources/MyBuildingsResourcePackage";
import { MyBuildingsTutorialResourcePackage } from "../resources/MyBuildingsTutorialResourcePackage";
import { UpgradeResourcePackage } from "../resources/UpgradeResourcePackage";
import { MyBuildingUpgradePage } from "./pages/MyBuildingUpgradePage";
import { ListInButton } from "../components/ListInButton";
import { SeeBuildingInButton } from "../components/SeeBuildingInButton";
import { PurchaseBuildingButton } from "../components/PurchaseBuildingButton";
import { VariableSaver } from "by/gamefactory/tools/variableSaver/VariableSaver";
import { PaymentResult } from "../../../../../web3/PaymentResult";
import { BuildingRightsType } from "../../../../networking/data/responses/user/BuildingRightsType";
import { DynamicResourcesPackage } from "../../../../../resources/DynamicResourcesPackage";
import { CancellationToken } from "../../../../../shared/async/CancellationToken";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { IFirstTimeExperienceModel } from "../../../common/models/IFirstTimeExperienceModel";
import Big from "big.js";
import { PriceHistoryButton } from "../../../common/components/PriceHistoryButton";

export class MyBuildingsPopupView extends BaseScalableView<boolean> {
    private static readonly RENTER_HEADER_TEXT = "RENTER'S BUILDING";
    private static readonly OWNER_HEADER_TEXT = "MY BUILDINGS";
    private static readonly EXIT_AREA_WIDTH = 114/*px*/;

    private readonly cancellationToken: CancellationToken;
    private readonly playerModel: IPlayerModel;
    private readonly myBuildingsController: IMyBuildingsController;
    private readonly gameController: IGameController;
    private readonly myBuildingsResources: MyBuildingsResourcePackage;
    private readonly upgradeResources: UpgradeResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly myBuildingsDynamicResources: DynamicResourcesPackage;
    private readonly renderer: Renderer;
    private readonly topPanel: TopPanel;
    private readonly buildingsListeners: Array<BuildingListeners>;
    private readonly tutorialResources: MyBuildingsTutorialResourcePackage;
    private readonly logger: ILogger;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly renterPolicyModel: IRenterPolicyModel;
    private readonly firstTimeExperienceController: IFirstTimeExperienceController;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly piggyAnimationPcs: PiggyAnimationPromiseHolder;
    private readonly variableSaver: VariableSaver;
    private readonly web3Model: Web3Model;
    private readonly guestModel: IGuestModel;

    private viewBackground: Sprite;
    private exitButton: DefaultButton;
    private headerText: Text;
    private buildings: Array<Building>;
    private nextBuilding: NextBuilding;
    private buildingsMoveStartPositionY: number;
    private scrollBox: UIScrollBox;
    private leftExitArea: Sprite;
    private scrollBar: VerticalScrollBar;
    private myBuildingsTutorial: MyBuildingsTutorial;
    private isFirstShow: boolean;
    private showMyBuildingsAnimation: gsap.core.Timeline;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        cancellationToken: CancellationToken,
        commonResources: CommonResourcesPackage,
        myBuildingsResources: MyBuildingsResourcePackage,
        upgradeResources: UpgradeResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        myBuildingsDynamicResources: DynamicResourcesPackage,
        tutorialResources: MyBuildingsTutorialResourcePackage,
        playerModel: IPlayerModel,
        renterPolicyModel: IRenterPolicyModel,
        web3Model: Web3Model,
        myBuildingsController: IMyBuildingsController,
        gameController: IGameController,
        firstTimeExperienceController: IFirstTimeExperienceController,
        firstTimeExperienceModel: IFirstTimeExperienceModel,
        analyticsProvider: IAnalyticProviderWrapper,
        piggyAnimationPcs: PiggyAnimationPromiseHolder,
        topPanel: TopPanel,
        renderer: Renderer,
        isFirstShow: boolean,
        asyncOperationNotification: AsyncOperationNotification,
        variableSaver: VariableSaver,
        canvasScaler: ICanvasScaler,
        logger: ILogger,
        guestModel: IGuestModel
    ) {
        super(operationControls, canvasScaler);
        this.cancellationToken = cancellationToken;
        this.playerModel = playerModel;
        this.myBuildingsController = myBuildingsController;
        this.gameController = gameController;
        this.myBuildingsResources = myBuildingsResources;
        this.upgradeResources = upgradeResources;
        this.commonResources = commonResources;
        this.myBuildingsDynamicResources = myBuildingsDynamicResources;
        this.renderer = renderer;
        this.topPanel = topPanel;
        this.tutorialResources = tutorialResources;
        this.isFirstShow = isFirstShow;
        this.logger = logger;
        this.guestModel = guestModel;
        this.analyticsProvider = analyticsProvider;
        this.soundResources = soundResources;
        this.renterPolicyModel = renterPolicyModel;
        this.firstTimeExperienceController = firstTimeExperienceController;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.piggyAnimationPcs = piggyAnimationPcs;
        this.variableSaver = variableSaver;
        this.web3Model = web3Model;
        this.buildingsListeners = [];
        this.buildings = [];
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.show.bind(this));

        this.renterPolicyModel.propertyChanged.add(this.onRenterPolicyModelPropertyChanged, this);
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
        this.scrollBox.onScroll.add(this.onScroll, this);
        this.exitButton.onUp.add(this.onExitButtonClick, this);
    }

    private async onRenterPolicyModelPropertyChanged(model: IRenterPolicyModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "penalties"):
                if (model.penalties.value != undefined) {
                    this.operationControls.complete(false);
                }
                break;
            case nameof(model, "previousCycle"):
                if (model.previousCycle.value.result == RenterPolicyCycleResult.INACTIVE_PERIOD_WITH_INSURANCE) {
                    this.operationControls.complete(false);
                }
                break;
        }
    }

    private onCancellationRequested(): void {
        this.operationControls.complete(false);
        this.destroy();
    }

    public async start(): Promise<void> {
        const buildings = this.playerModel.buildings.value;
        if (buildings.length > 0 && buildings[0].upgrades?.length > 0 && !this.isFirstShow) {
            this.showMyBuildingsAnimation.progress(1);
            await this.onBuildingClick(this.buildings[0], this.playerModel.buildings.value[0], true);
        }
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.addChild(this.viewBackground);
        const header = this.playerModel.buildingRights.value === BuildingRightsType.OWNER ? MyBuildingsPopupView.OWNER_HEADER_TEXT : MyBuildingsPopupView.RENTER_HEADER_TEXT;
        this.headerText = new Text(header, new HeaderTextStyle());
        this.addChild(this.headerText);

        this.buildings = this.playerModel.buildings.value.map((value) => this.buildingContainerFactory(value));
        this.nextBuilding = new NextBuilding(this.myBuildingsResources, this.canvasScaler.referenceWidth);
        this.scrollBox = new UIScrollBox({
            width: this.canvasScaler.referenceWidth,
            height: this.canvasScaler.referenceHeight - 300,
            type: 'vertical',
            vertPadding: 30,
            elementsMargin: 52
        });
        this.scrollBox.addItems([...this.buildings, this.nextBuilding]);
        this.addChild(this.scrollBox);
        this.leftExitArea = new Sprite(Texture.EMPTY);
        this.leftExitArea.on("pointerdown", this.onExitButtonClick, this);
        this.leftExitArea.eventMode = "static";
        this.addChild(this.leftExitArea);

        this.scrollBar = new VerticalScrollBar({
            lift: this.myBuildingsResources.assets_views_mybuildings_scrollbar_lift_png,
            line: this.myBuildingsResources.assets_views_mybuildings_scrollbar_line_png,
            arrow: this.myBuildingsResources.assets_views_mybuildings_scrollbar_arrow_png
        }, this.canvasScaler.referenceHeight - 300, (this.scrollBox.items[0].height - 30) * this.scrollBox.items.length - 1, 0, -8);
        this.addChild(this.scrollBar);

        this.exitButton = new DefaultButton(this.commonResources.assets_common_ui_back_png);
        this.addChild(this.exitButton);

        if (this.isFirstShow && this.tutorialResources) {
            this.myBuildingsTutorial = new MyBuildingsTutorial(this.tutorialResources, this.commonResources, this.soundResources);
            this.addChild(this.myBuildingsTutorial);
            this.topPanel.interactiveChildren = false;
        }

        this.topPanel.bringToFront();
    }

    private onScroll(value: number): void {
        this.scrollBar.updateScroll(value);
        this.scrollBox.items.forEach((building: Container) => building.eventMode = 'dynamic');
    }

    private buildingContainerFactory(buildingModel: IBuildingModel): Building {
        const building = new Building(
            this.myBuildingsResources,
            this.upgradeResources,
            this.commonResources,
            this.myBuildingsDynamicResources,
            buildingModel,
            this.canvasScaler.referenceWidth,
            this.renderer,
            this.playerModel,
            this.isFirstShow
        );
        const buildingListeners = {
            onBuildingClick: () => this.onBuildingClick(building, buildingModel),
            onPriceHistoryClick: (priceHistoryButton: PriceHistoryButton) => this.onPriceHistoryClick(priceHistoryButton, buildingModel),
            onListInButtonClick: (listInButton: ListInButton) => this.onListInButtonClick(listInButton, buildingModel),
            onSeeBuildingInClick: (seeButton: SeeBuildingInButton) => this.onSeeBuildingInClick(seeButton, buildingModel),
            onPurchaseBuildingClick: (purchaseButton: PurchaseBuildingButton) => this.onPurchaseBuildingInClick(purchaseButton, buildingModel),
        };
        building.onBuildingClick.add(buildingListeners.onBuildingClick);

        building.onPriceHistoryClick.add(buildingListeners.onPriceHistoryClick);
        building.onPriceHistoryButtonDown?.add(this.onBuildingButtonDown, this);
        building.onPriceHistoryButtonOut?.add(this.onBuildingButtonOut, this);

        building.onListInClick.add(buildingListeners.onListInButtonClick);
        building.onListInButtonDown?.add(this.onBuildingButtonDown, this);
        building.onListInButtonOut?.add(this.onBuildingButtonOut, this);

        building.onSeeBuildingInClick.add(buildingListeners.onSeeBuildingInClick);
        building.onSeeBuildingButtonDown?.add(this.onBuildingButtonDown, this);
        building.onSeeBuildingButtonOut?.add(this.onBuildingButtonOut, this);

        building.onPurchaseBuildingClick.add(buildingListeners.onPurchaseBuildingClick);
        building.onPurchaseButtonDown?.add(this.onBuildingButtonDown, this);
        building.onPurchaseButtonOut?.add(this.onBuildingButtonOut, this);

        building.onUpgradeClick?.add(buildingListeners.onBuildingClick);
        building.onUpgradeButtonDown?.add(this.onBuildingButtonDown, this);
        building.onUpgradeButtonOut?.add(this.onBuildingButtonOut, this);

        this.buildingsListeners.push(buildingListeners);
        return building;
    }

    private async onBuildingClick(selectedBuilding: Building, buildingModel: IBuildingModel, force: boolean = false): Promise<void> {
        if (!force) {
            this.soundResources.assets_sounds_myBuildings_upgrade_click_mp3.play();
        }
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.scrollBox.stopScrolling();
        this.scrollBox.eventMode = 'static';
        if (this.isFirstShow && this.tutorialResources) {
            this.exitButton.eventMode = 'static';
            this.leftExitArea.eventMode = 'static';
            await this.myBuildingsTutorial.hide();
        }
        if (!force) {
            const animationTimeLine = new gsap.core.Timeline();
            animationTimeLine.to(this.scrollBox, { y: this.scrollBox.y - 50, duration: 0.15, repeat: 1, yoyo: true });
            await animationTimeLine;
            animationTimeLine.kill();
        }
        await this.switchToUpgradePage(selectedBuilding, buildingModel);
        await this.showComponents();
        this.removeChild(inputBlocker);
    }

    private onBuildingButtonDown(): void {
        this.scrollBox.eventMode = 'auto';
    }

    private onBuildingButtonOut(): void {
        if (!this.isFirstShow) {
            this.scrollBox.stopScrolling();
            this.scrollBox.eventMode = 'static';
        }
    }

    private async onPriceHistoryClick(priceHistoryButton: PriceHistoryButton, buildingModel: IBuildingModel): Promise<void> {
        this.scrollBox.stopScrolling();
        this.scrollBox.eventMode = 'static';
        priceHistoryButton.eventMode = "none";
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.topPanel.bringToOriginParent(true);
        await this.myBuildingsController.showPriceHistoryScene(buildingModel.purchaseHistory.value);
        this.removeChild(inputBlocker);
        this.topPanel.bringToFront();
        priceHistoryButton.eventMode = "static";
    }

    private async onListInButtonClick(listIn: ListInButton, buildingModel: IBuildingModel): Promise<void> {
        this.scrollBox.stopScrolling();
        this.scrollBox.eventMode = 'static';
        this.scrollBox.setWheelScrollable(false);
        listIn.eventMode = "auto";
        try {
            await this.myBuildingsController.updateBuildingsInventory();
            if (!this.web3Model.buildingExistOnInternalWallet(buildingModel.tokenId.value)) {
                const topPanelOnNotification = this.topPanel.onNotificationLayer;
                if (topPanelOnNotification) {
                    this.topPanel.bringToOriginParent();
                }
                await this.myBuildingsController.showListInMpNotificationPopup();
                if (topPanelOnNotification) {
                    this.topPanel.bringToFront();
                }
                this.scrollBox.setWheelScrollable(true);
                listIn.eventMode = "static";
                return;
            }
            if (await this.myBuildingsController.listInMarketPlace(buildingModel.id.value, buildingModel.tokenId.value, null, buildingModel.purchaseHistory.value, this.cancellationToken)) {
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("A player add his building to MP", this.analyticsProvider.currentEventIndex));
                await this.tranitToMarketplace(buildingModel);
            } else {
                this.topPanel.bringToFront();
            }
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            listIn.eventMode = "static";
            this.scrollBox.setWheelScrollable(true);
        }
    }

    private async onSeeBuildingInClick(seeButton: SeeBuildingInButton, buildingModel: IBuildingModel): Promise<void> {
        seeButton.eventMode = "none";
        this.tranitToMarketplace(buildingModel);
    }

    private async onPurchaseBuildingInClick(purchaseButton: PurchaseBuildingButton, buildingModel: IBuildingModel): Promise<void> {
        this.scrollBox.stopScrolling();
        this.scrollBox.eventMode = 'static';
        purchaseButton.eventMode = "none";
        try {
            this.asyncOperationNotification.show();
            const result = await this.myBuildingsController.paymentBuildingWithMint(buildingModel, this.cancellationToken);
            if (result == PaymentResult.SUCCESS && this.playerModel.buildingRights.value == BuildingRightsType.RENTER) {
                location.reload();
            }
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            this.asyncOperationNotification.hide();
            purchaseButton.eventMode = "static";
        }
    }

    private async tranitToMarketplace(buildingModel: IBuildingModel): Promise<void> {
        this.scrollBox.stopScrolling();
        this.scrollBox.eventMode = 'static';
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.myBuildingsController.showMarketPlacePopupScene(buildingModel.area.value);
        this.operationControls.complete(false);
    }

    private async show(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.soundResources.assets_sounds_myBuildings_upgrade_zoom_in_mp3.play();
        this.showMyBuildingsAnimation = new gsap.core.Timeline();
        this.showMyBuildingsAnimation.fromTo(this, { alpha: 0 }, {
            alpha: 1,
            duration: 0.2,
            ease: "power1.in"
        });
        this.showMyBuildingsAnimation.fromTo(this.scrollBox, { y: this.buildingsMoveStartPositionY }, {
            y: this.scrollBox.y,
            duration: 0.5,
            ease: "back.out(2.5)"
        }, "<");
        this.showMyBuildingsAnimation.fromTo(this.exitButton, { x: this.canvasScaler.getSize().width * 0.9, alpha: 0 }, { x: this.exitButton.x, alpha: 1, duration: 0.3, ease: "power3.inout" }, "<90%");
        this.showMyBuildingsAnimation.fromTo(this.headerText, { alpha: 0 }, { alpha: 1, duration: 0.1 }, "<80%");
        if (this.isFirstShow && this.tutorialResources) {
            this.exitButton.eventMode = 'none';
            this.leftExitArea.eventMode = 'none';
            this.scrollBox.eventMode = "auto";
            this.scrollBox.setWheelScrollable(false);
            this.myBuildingsTutorial.show();
        }
        this.showMyBuildingsAnimation.addLabel("showBuilding");
        this.buildings.map((building) => this.showMyBuildingsAnimation.add(building.getShowAnimation().play()), "showBuilding");
        await this.showMyBuildingsAnimation.play();
        this.showMyBuildingsAnimation.clear();
        this.onScroll(0);
        this.removeChild(inputBlocker);
    }

    private async switchToUpgradePage(selectedBuilding: Building, buildingModel: IBuildingModel): Promise<void> {
        this.soundResources.assets_sounds_myBuildings_upgrade_zoom_in_mp3.play();
        this.scrollBox.visible = false;
        this.scrollBox.scrollTop();
        this.scrollBox.removeItems();
        this.scrollBar.visible = false;
        this.hideComponents(selectedBuilding);

        let tokenTutorialRequired = false;
        if (this.firstTimeExperienceModel.tooltipsInfo.value?.firstUpgradeByTokens && !this.isFirstShow && this.playerModel.tokens.value) {
            if (buildingModel.needToFix.value) {
                const fixItemData = buildingModel.upgrades.find((item) => item.elements.value.needToFix > 0 && item.lock.value.status == false);
                tokenTutorialRequired = new Big(this.playerModel.tokens.value).gte(fixItemData.fixCostInTokens.value);
            } else {
                const upgradeItemData = buildingModel.upgrades.find((item) => item.elements.value.needToUpgrade > 0 && item.lock.value.status == false);
                tokenTutorialRequired = new Big(this.playerModel.tokens.value).gte(upgradeItemData.upgradeCostInTokens.value);
            }
        }

        const pageCS = new PromiseCompletionSource<void>();
        const upgradePage = new MyBuildingUpgradePage(
            this.cancellationToken,
            this.commonResources,
            this.upgradeResources,
            this.myBuildingsDynamicResources,
            pageCS,
            this.canvasScaler,
            buildingModel,
            this.myBuildingsController,
            this.gameController,
            selectedBuilding,
            this.logger,
            this.analyticsProvider,
            this.topPanel,
            this.soundResources,
            this.playerModel,
            this.isFirstShow,
            this.firstTimeExperienceController,
            this.firstTimeExperienceModel,
            this.operationControls,
            tokenTutorialRequired,
            this.piggyAnimationPcs,
            this.variableSaver,
            this.guestModel,
            this.tutorialResources
        );
        selectedBuilding.position.copyFrom(this.scrollBox.position);
        this.addChild(upgradePage);
        await selectedBuilding.showUpgradeStyle();
        await pageCS.result;

        this.scrollBox.addItems([...this.buildings, this.nextBuilding]);
        this.scrollBox.visible = true;
        this.scrollBar.visible = true;
        this.removeChild(upgradePage);
        upgradePage.destroy();
        this.isFirstShow = false;
    }

    private async showComponents(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.set(this.exitButton, { alpha: 1 });
        animationTimeLine.to(this.headerText, { y: this.headerText.y - this.headerText.getLocalBounds().width, angle: 0, alpha: 1, duration: 0.4 });
        animationTimeLine.to(this.headerText.scale, { x: 1, y: 1, duration: 0.4 }, "<");
        this.buildings.forEach((item) => {
            animationTimeLine.to(item, { alpha: 1, duration: 0.3 }, "<");
        });
        animationTimeLine.to(this.nextBuilding, { alpha: 1, duration: 0.3 }, "<");
        animationTimeLine.to(this.scrollBar, { alpha: 1, duration: 0.3 }, "<");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private async hideComponents(selectedBuilding: Building): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.buildings.forEach((item) => {
            if (item != selectedBuilding) {
                animationTimeLine.to(item, { alpha: 0, duration: 0.3 }, "<");
            }
        });
        animationTimeLine.to(this.nextBuilding, { alpha: 0, duration: 0.3 }, "<");
        animationTimeLine.to(this.headerText, { y: this.headerText.y + this.headerText.width, angle: -75, alpha: 0, duration: 0.4 }, "<");
        animationTimeLine.to(this.headerText.scale, { x: 0, y: 0, duration: 0.4 }, "<");
        animationTimeLine.to(this.exitButton, { alpha: 0, duration: 0.4 }, "<");
        animationTimeLine.to(this.scrollBar, { alpha: 0, duration: 0.4 }, "<");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private async onExitButtonClick(): Promise<void> {
        this.soundResources.assets_sounds_myBuildings_upgrade_X_button_click_mp3.play();
        this.hideView();
    }

    private async hideView(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
        this.soundResources.assets_sounds_myBuildings_upgrade_zoom_out_mp3.play();
        this.operationControls.complete(false);
    }

    protected layout(width: number, height: number): void {
        this.exitButton.position.set(74, 263 - this.exitButton.height * 0.5);
        this.headerText.position.set(this.exitButton.position.x + 80, 267 - this.headerText.height * 0.5);
        this.buildingsMoveStartPositionY = height;
        this.leftExitArea.width = MyBuildingsPopupView.EXIT_AREA_WIDTH;
        this.leftExitArea.height = height;
        this.leftExitArea.position.set(0, this.topPanel.height);
        this.scrollBox.position.set(0, 305);
        this.scrollBar.position.set(width - this.scrollBar.width - 32, 460);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.scrollBox?.onScroll.remove(this.onScroll, this);
        this.showMyBuildingsAnimation?.kill();
        this.topPanel.bringToOriginParent();
        this.leftExitArea.off("pointerdown", this.onExitButtonClick, this);
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.scrollBox.onScroll.remove(this.onScroll, this);
        this.exitButton.onUp.remove(this.onExitButtonClick, this);
        this.renterPolicyModel.propertyChanged.remove(this.onRenterPolicyModelPropertyChanged, this);
        for (let i = 0; i < this.buildings.length; i++) {
            this.buildings[i].onBuildingClick.remove(this.buildingsListeners[i].onBuildingClick);
            this.buildings[i].onPriceHistoryClick.remove(this.buildingsListeners[i].onPriceHistoryClick);
            this.buildings[i].onListInClick.remove(this.buildingsListeners[i].onListInButtonClick);
            this.buildings[i].onSeeBuildingInClick.remove(this.buildingsListeners[i].onSeeBuildingInClick);
            this.buildings[i].onPurchaseBuildingClick.remove(this.buildingsListeners[i].onPurchaseBuildingClick);
            this.buildings[i].onUpgradeClick?.remove(this.buildingsListeners[i].onBuildingClick);
            this.buildings[i].onUpgradeButtonDown?.remove(this.onBuildingButtonDown, this);
            this.buildings[i].onUpgradeButtonOut?.remove(this.onBuildingButtonOut, this);
            this.buildings[i].onPriceHistoryButtonDown?.remove(this.onBuildingButtonDown, this);
            this.buildings[i].onPriceHistoryButtonOut?.remove(this.onBuildingButtonOut, this);
            this.buildings[i].onListInButtonDown?.remove(this.onBuildingButtonDown, this);
            this.buildings[i].onListInButtonOut?.remove(this.onBuildingButtonOut, this);
            this.buildings[i].onPurchaseButtonDown?.remove(this.onBuildingButtonDown, this);
            this.buildings[i].onPurchaseButtonOut?.remove(this.onBuildingButtonOut, this);
            this.buildings[i].onSeeBuildingButtonDown?.remove(this.onBuildingButtonDown, this);
            this.buildings[i].onSeeBuildingButtonOut?.remove(this.onBuildingButtonOut, this);
            this.buildings[i].destroy(options);
        }
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 60,
        }));
    }
}

type BuildingListeners = {
    onBuildingClick: () => void;
    onPriceHistoryClick: (priceHistoryButton: PriceHistoryButton) => void;
    onListInButtonClick: (listInButton: ListInButton) => void;
    onSeeBuildingInClick: (seeButton: SeeBuildingInButton) => void;
    onPurchaseBuildingClick: (purchaseButton: PurchaseBuildingButton) => void;
}
